/**
 * The page ground: the colour behind a screen, which the body also paints under
 * the status bar and the home indicator (index.css `--root-bg-color`).
 * Contract: docs/design/dark-mode-plan.md §5
 *
 * Replaces the `setRootBackground('#ffffff')` calls that wrote a literal colour
 * and restored a hard-coded eggshell — or never restored anything, so the strip
 * above a screen depended on which screen you came from. A ground is now a role
 * name, so it flips with the theme, and grounds form a stack: the most recently
 * mounted one wins, and unmounting hands back to whatever is underneath. An
 * empty stack falls back to the CSS default.
 *
 * `AppLayout` sets its route's ground in a layout effect, which runs before any
 * page's passive effect — so a page (or an overlay like the guide chapter) that
 * sets its own ground always wins over its route's.
 */
import { useEffect, useLayoutEffect } from 'react';

export type PageGround = 'ground' | 'ground-session' | 'surface';

/** Literal class names so Tailwind generates them. */
export const PAGE_GROUND_BG: Record<PageGround, string> = {
    ground: 'bg-ground',
    'ground-session': 'bg-ground-session',
    surface: 'bg-surface',
};

const stack: Array<{ id: symbol; value: string }> = [];

function paint(): void {
    const top = stack[stack.length - 1];
    const root = document.documentElement.style;
    if (top) root.setProperty('--root-bg-color', top.value);
    else root.removeProperty('--root-bg-color');
}

function push(value: string): () => void {
    const id = Symbol(value);
    stack.push({ id, value });
    paint();
    return () => {
        const index = stack.findIndex((entry) => entry.id === id);
        if (index !== -1) stack.splice(index, 1);
        paint();
    };
}

/** Any CSS colour value — for the product loading screen, whose GreekBrain colour is not a role. */
export function useRootBackground(value: string | null): void {
    useEffect(() => (value ? push(value) : undefined), [value]);
}

/** A screen's ground, by role. */
export function usePageGround(ground: PageGround | null): void {
    useEffect(() => (ground ? push(`var(--color-${ground})`) : undefined), [ground]);
}

/** A route's ground — runs first, so the page's own ground wins. Used by AppLayout. */
export function useRouteGround(ground: PageGround | null): void {
    useLayoutEffect(() => (ground ? push(`var(--color-${ground})`) : undefined), [ground]);
}
